import React, {useEffect, useRef, useState} from 'react';
import {Table, Spinner, Button} from 'react-bootstrap';
import {deleteMotion} from 'api/MotionsApi';
import ModalDialog from 'components/Dialogs/ModalDialog';
import {capacityUnits, motionTypes} from 'constants/dataConstants';
import {useAppContext} from 'contexts/AppContext';
import {isSystemAdmin} from 'utils/Auth';
import {isEmpty} from 'utils/Utils';

export default function MotionsList({
    pageState, currentVersion, currentConfiguration, onDataUpdate, onDetailsClick, onAddButtonClick
}) {
    const isMounted = useRef(false);
    const motionObject = useRef(null);
    const {showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogBody, setDeleteDialogBody] = useState(<></>);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [motions, setMotions] = useState([]);
    const [motionNames, setMotionNames] = useState({});
    const [vfdNames, setVfdNames] = useState({});
    const isEditable = isSystemAdmin() && currentVersion.status === 0;

    useEffect(() => {
        isMounted.current = true;
        getMotions();
        return () => isMounted.current = false;
    }, [pageState, currentVersion, currentConfiguration]);

    const getMotions = () => {
        if (isMounted.current) {
            setIsLoading(true);
            if (!isEmpty(currentConfiguration) && !isEmpty(currentConfiguration.crane_details)) {
                setMotions(currentConfiguration.crane_details.motion_config);
                let names = {};
                currentConfiguration.crane_details.motion_config.map(motion => {
                    names[motion.id] = motion.motion_name;
                });
                setMotionNames(names);
                names = {};
                currentConfiguration.crane_details.vfd_config.map(vfd => {
                    names[vfd.vfd_uuid] = vfd.vfd_name;
                });
                setVfdNames(names);
            }
            setIsLoading(false);
        }
    };

    const handleDeleteClick = motion => {
        motionObject.current = motion;
        setDeleteDialogBody(<p>Do you really want to delete the motion "<b>{motion.motion_name}</b>"?</p>);
        setShowDeleteDialog(true);
    };

    const handleModalClosed = () => {
        setShowDeleteDialog(false);
    };

    const handleModalConfirmed = () => {
        setIsLoading(true);
        const onDone = () => isMounted.current && setIsLoading(false);
        deleteMotion(pageState.craneId, motionObject.current.id, handleSuccessResponse, handleApiError, onDone);
        handleModalClosed();
    };

    const handleSuccessResponse = (response) => {
        if (isMounted.current) {
            showMessage('success', 'Motion configuration has been successfully saved');
            onDataUpdate();
        }
    };

    let addButton;
    if (isEditable) addButton = <button className="btn btn-add" onClick={onAddButtonClick}>Add</button>;

    let tableContent;
    if (isLoading) tableContent = <tr><td colSpan={isEditable ? 7 : 6} className="text-center"><Spinner animation="border" /></td></tr>;
    else if (motions.length === 0) tableContent = <tr><td colSpan={isEditable ? 7 : 6}>No motions found</td></tr>;
    else tableContent = motions.map(motion => {
        let motionVfds = motion.motion_vfd_mapping ? motion.motion_vfd_mapping.map(item => vfdNames[item]).join(', ') : '-';
        return (
            <tr key={motion.id}>
                <td>{motion.motion_name}</td>
                <td>{motionTypes[motion.motion_type]}</td>
                <td>{motion.motion_capacity + ' ' + capacityUnits[motion.motion_capacity_unit]}</td>
                <td>{motionVfds}</td>
                <td>{motionNames[motion.motion_reference]}</td>
                <td className="text-center">
                    <span className="text-primary text-link" onClick={() => onDetailsClick(motion)}>{isEditable ? 'Edit' : 'View'}</span>
                </td>
                {isEditable &&
                    <td className="text-center">
                        <span className="text-primary text-link" onClick={() => handleDeleteClick(motion)}>Delete</span>
                    </td>}
            </tr>
        );
    });

    return (
        <>
            <ModalDialog title="Deleting motion" body={deleteDialogBody} show={showDeleteDialog} onHide={handleModalClosed}>
                <Button variant="secondary" onClick={handleModalClosed}>No</Button>
                <Button variant="danger" onClick={handleModalConfirmed}>Yes</Button>
            </ModalDialog>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Motion Type</th>
                        <th>Capacity</th>
                        <th>VFD</th>
                        <th>Installed On</th>
                        <th colSpan={isEditable ? 2 : 1} className="text-center">{addButton}</th>
                    </tr>
                </thead>
                <tbody>{tableContent}</tbody>
            </Table>
        </>
    );
}
